import { getSDK } from '../../services/sdk-instance';

const SVG_NS = 'http://www.w3.org/2000/svg';

export type MediaPreviewFormat = 'svg' | 'png' | 'jpeg' | 'webp';

const PREVIEW_MIME: Record<MediaPreviewFormat, string> = {
  svg: 'image/svg+xml',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** HTML-tree SVG omits xmlns; `<img>` and file downloads cannot parse without it (media ADR-007). */
export function ensureStandaloneSvg(bytes: ArrayBuffer): ArrayBuffer {
  const text = new TextDecoder().decode(bytes);
  const match = text.match(/<svg[\s>]/i);
  if (!match || match.index === undefined) {
    throw new Error('svg preview is not an svg document');
  }
  const start = match.index;
  const gt = text.indexOf('>', start);
  if (gt === -1) {
    throw new Error('svg preview has no root tag');
  }
  if (/\sxmlns\s*=/i.test(text.slice(start, gt))) {
    return bytes;
  }
  const fixed =
    text.slice(0, start) +
    text.slice(start, gt).replace(/^<svg/i, `<svg xmlns="${SVG_NS}"`) +
    text.slice(gt);
  return new TextEncoder().encode(fixed).buffer;
}

export function previewFormatFor(
  formats: readonly string[],
  warnings: readonly string[],
): MediaPreviewFormat | undefined {
  const raster = formats.includes('png');
  const vector = formats.includes('svg');
  const jpeg = formats.includes('jpeg');
  const webp = formats.includes('webp');
  if (formats.includes('html') && !raster) return undefined;
  if (warnings.length > 0 && raster) return 'png';
  if (vector) return 'svg';
  if (raster) return 'png';
  if (jpeg) return 'jpeg';
  if (webp) return 'webp';
  return undefined;
}

export async function loadMediaPreview(
  mediaId: string,
  format: MediaPreviewFormat,
  version: number,
): Promise<string> {
  const raw = await getSDK().media.content(mediaId, { format, version });
  const bytes = format === 'svg' ? ensureStandaloneSvg(raw) : raw;
  return URL.createObjectURL(new Blob([bytes], { type: PREVIEW_MIME[format] }));
}
